<template>
  <footer class="the-footer">
    <div class="container">
      <div class="inner">
        <div class="md:px-2">
          <NuxtLink
            class="logo"
            :class="logoHeight ? logoHeight : 'h-8'"
            :to="localePath('/')"
          >
            <NuxtImg v-no-right-click :src="companyInfo.logo" alt="logo" />
          </NuxtLink>
          <p class="description">
            {{ t(companyInfo.about_company) }}
          </p>
          <div class="inline-block mb-[40px]">
            <span class="company-link-title">
              {{ t("footer.payment") + ":" }}
            </span>
            <NuxtImg
              v-no-right-click
              src="https://tw028jd0.turpal.com/assets/accept-payment.png"
              alt="accept payment"
              class="h-5"
            />
          </div>
        </div>
        <div class="flex flex-col md:flex-row gap-5 md:gap-24">
          <div class="links-list">
            <h6 class="title">
              {{ companyInfo.name }}
            </h6>
            <ul>
              <li class="link-item" v-if="elfsightForm">
                <button class="menu-button" @click="showElfsightForm = true">
                  {{ t("form.title") }}
                </button>
              </li>
              <li
                v-for="(item, index) in footerConfig.public.footer.items"
                :key="index"
                class="link-item"
              >
                <NuxtLink :to="localePath(item.target)">
                  {{ t(item?.title) }}
                </NuxtLink>
              </li>
            </ul>
          </div>
          <div class="max-md:mt-5">
            <div
              v-if="
                hasSocialMedia ||
                info?.whatsapp_number ||
                info?.telephones ||
                info?.email
              "
            >
              <h5 class="text-mobile-heading-5 md:text-mobile-heading-5 mb-5">
                {{ t("getHelp") }}
              </h5>
              <a
                v-if="info?.whatsapp_number"
                :href="`https://wa.me/${replaceSpace(info?.whatsapp_number)}`"
                target="_blank"
                class="mb-5 flex items-center text-mobile-heading-6 md:text-heading-6"
              >
                <icon-whatsapp class="!text-[16px] mr-2" />
                <span dir="ltr">{{ info?.whatsapp_number }}</span>
              </a>
              <a
                v-if="info?.telephones?.length"
                :href="`tel:${info?.telephones[0]}`"
                class="mb-5 flex items-center text-mobile-heading-6 md:text-heading-6"
              >
                <icon-call class="!text-[16px] mr-2" />
                <span dir="ltr">{{ info?.telephones[0] }}</span>
              </a>
              <a
                v-if="info?.email"
                :href="`mailto:${info?.email}`"
                class="mb-5 md:mb-6 flex items-center text-mobile-heading-6 md:text-heading-6"
              >
                <icon-email class="!text-[16px] mr-2" />
                {{ info?.email }}
              </a>
              <div v-if="hasSocialMedia" class="mb-14 md:mb-10">
                <TheFooterSocialMedias :socials="companyInfo.social_medias" />
                <div v-if="companyInfo.outer_links" class="flex flex-col mt-10">
                  <span
                    class="text-gray-4 text-mobile-paragraph-2 md:text-paragraph-2 mb-3 title-font"
                  >
                    {{ t("footer.weAreListed") }}:
                  </span>
                  <div class="flex flex-col md:flex-row">
                    <NuxtLink
                      v-for="outerLink in companyInfo.outer_links"
                      :key="outerLink?.title"
                      :to="outerLink.target"
                      target="_blank"
                      class="flex items-center gap-2.5 mb-2 md:mb-0 md:me-5 cursor-pointer"
                    >
                      <NuxtImg
                        v-no-right-click
                        :src="outerLink.icon"
                        :alt="outerLink?.title"
                        width="30"
                      />
                      <span
                        class="text-gray-4 text-mobile-paragraph-2 md:text-paragraph-2 title-font"
                      >
                        {{ outerLink?.title }}
                      </span>
                    </NuxtLink>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="rights">
        {{ t("allRightsReserved") }}
      </div>
    </div>
    <BaseSimpleModal
      custom-class="!w-[554px] h-full !bg-body-bg !p-2 md:!p-4 !mx-2.5"
      close-btn-class="text-white m-2 text-[27px]"
      v-model:modal-show="showElfsightForm"
    >
      <template #content>
        <div :class="`${elfsightForm}`" data-elfsight-app-lazy></div>
      </template>
    </BaseSimpleModal>
  </footer>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import customFetch from "../../../composables/customFetch";
import type { ICompanyInfo } from "../../../types/general";
import TheFooterSocialMedias from "./TheFooterSocialMedias.vue";
import { useAsyncData, useRuntimeConfig } from "nuxt/app";
import useFooterSocialMedias from "../../../composables/useFooterSocialMedias";

const localePath = useLocalePath();
const config = useRuntimeConfig() as unknown as RuntimeConfiguration;
const { t } = useI18n();

defineProps({ type: { type: Number, default: 1 } });

function replaceSpace(item: string) {
  if (!item) return "";
  return item.replace(/\s+/g, "");
}

const footerConfig = ref<RuntimeConfiguration>(config);
const { fetchBlog, blogs } = useBlog();
const { data: blogsData } = await useAsyncData("footer-blogs", async () => {
  await fetchBlog();
  return blogs.value;
});
if (!blogsData.value?.length) {
  footerConfig.value.public.footer.items =
    footerConfig.value.public.footer.items?.filter(
      (item) => item.target !== "/blog"
    );
}
const elfsightForm = footerConfig.value.public.footer.elfsightForm || null;

const logoHeight = footerConfig.value.public.footer.logoHeightSize ?? null;

const socialMedias = await useFooterSocialMedias(footerConfig.value.public.footer.socialLinks);
const companyInfo = {
  name: footerConfig.value.public.name,
  about_company: footerConfig.value.public.footer.text,
  logo: footerConfig.value.public.footer.logo,
  get social_medias() {
    return socialMedias.value;
  },
  outer_links: footerConfig.value.public.footer.outerLinks,
};
const showElfsightForm = ref<boolean>(false);

const hasSocialMedia = computed(() => {
  return Object.keys(companyInfo.social_medias).length;
});
const { locale } = useI18n();
const { data: info } = await useAsyncData<ICompanyInfo>(
  `about-${locale.value}`,
  async () => customFetch(`api/company-info?lang=${locale.value}`),
);
onMounted(() => {
  if (
    !document.querySelector(
      'script[src="https://static.elfsight.com/platform/platform.js"]',
    )
  ) {
    const script = document.createElement("script");
    script.src = "https://static.elfsight.com/platform/platform.js";
    script.async = true;
    document.body.appendChild(script);
  }
});
</script>

<style scoped>
.the-footer {
  @apply bg-footer-background;
}
.the-footer .inner {
  @apply pb-8 md:pb-11 pt-[40px] md:pt-[90px] text-paragraph-2
  flex flex-col md:flex-row justify-between items-start
   text-footer-text-color;
}
.the-footer .inner .logo {
  @apply inline-flex mb-5 block w-full;
}
.the-footer .inner .logo img {
  @apply h-full w-auto rounded-none object-contain max-md:max-w-[60%];
}
.the-footer .inner .description {
  @apply text-mobile-heading-5 lg:text-heading-5 md:max-w-[17rem] mb-6;
}

.the-footer .inner .links-list {
  @apply md:px-2 xs:max-md:w-full;
}

.the-footer .inner .links-list .title {
  @apply md:mb-5 py-5 md:py-0
 flex justify-between items-center
 xs:max-md:cursor-pointer
 text-mobile-heading-5 md:text-heading-5;
}

.the-footer .inner .links-list .link-item {
  @apply text-mobile-heading-6 lg:text-heading-6 mb-5;
}

.the-footer .inner .description,
.the-footer .inner .links-list .title .the-footer .inner .link-item {
  @apply text-footer-text-color;
}
.the-footer .inner .company-link-title {
  @apply block text-mobile-heading-6 lg:text-heading-6 text-footer-text-color mb-2.5;
}
.the-footer .inner .b2b-button {
  @apply border border-primary-1 w-max px-3 py-2 rounded-md text-primary-1 block text-paragraph-2;
}

.the-footer .rights {
  @apply text-footer-text-color pb-10 md:pb-5 text-paragraph-2;
}
</style>
